import PT from 'prop-types';
import React from 'react';

import downloadSrc from '~/assets/images/cta/download.svg';
import { FileMimeType } from '~/enums/FileMimeType';
import { HtmlDocumentViewer } from '~/features/document-viewer/HtmlDocumentViewer/HtmlDocumentViewer';
import { useIntl } from 'react-intl';
import Box from '~/ui/components/Box/Box';
import CtaSection from '~/ui/components/CtaSection/CtaSection';
import { downloadFile } from '~/utils/files';

import DocumentImageViewer from './DocumentImageViewer';
import DocumentPdfViewer from './DocumentPdfViewer';

function DocumentViewer({
  file,
  htmlProps = {},
  maxWidth = 'document-max-width',
  noFileMessage = undefined,
  width = '100%',
  onDownloadFile = downloadFile,
}) {
  const intl = useIntl();

  function Container({ children }) {
    return (
      <Box
        p={16}
        sx={{
          background: 'white',
          boxShadow: 'depth-2',
          maxWidth,
          width,
        }}
      >
        {children}
      </Box>
    );
  }

  const defaultNoFileMessage = intl.formatMessage({
    id: 'DocumentViewer.noFileProvided',
    defaultMessage: 'No file provided',
    description:
      'Message shown when no file is provided to the document viewer',
  });

  if (!file) {
    return <Container>{noFileMessage ?? defaultNoFileMessage}</Container>;
  }

  let viewer;
  switch (file.type) {
    case FileMimeType.Html:
      viewer = <HtmlDocumentViewer file={file} {...htmlProps} />;
      break;
    case FileMimeType.Pdf:
      viewer = <DocumentPdfViewer file={file} />;
      break;
    case FileMimeType.Jpeg:
    case FileMimeType.Jpg:
    case FileMimeType.Png:
    case FileMimeType.Webp:
      viewer = <DocumentImageViewer file={file} />;
      break;
    default: {
      viewer = (
        <CtaSection
          description={intl.formatMessage({
            id: 'DocumentViewer.downloadDescription',
            defaultMessage: 'Download the contract for use offline.',
            description: 'Description text for downloading contract',
          })}
          imageSrc={downloadSrc}
          actionButton={{
            text: intl.formatMessage({
              id: 'DocumentViewer.downloadButton',
              defaultMessage: 'Download',
              description: 'Button text for downloading file',
            }),
            onClick: () => onDownloadFile(file),
          }}
          title={intl.formatMessage({
            id: 'DocumentViewer.previewUnavailable',
            defaultMessage: 'File preview is unavailable for this file type',
            description: 'Title shown when file preview is not available',
          })}
        />
      );
    }
  }

  return <Container>{viewer}</Container>;
}

DocumentViewer.propTypes = {
  // JS File/Blob object
  file: PT.oneOfType([PT.instanceOf(File), PT.instanceOf(Blob)]),
  htmlProps: PT.shape({
    activeElementId: PT.string,
    highlights: PT.arrayOf(
      PT.shape({
        id: PT.string.isRequired,
        className: PT.string,
      }),
    ),
  }),
  maxWidth: PT.string,
  noFileMessage: PT.string,
  width: PT.string,
  onDownloadFile: PT.func,
};

export default DocumentViewer;
